import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import { Close, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logout } from '../config/auth';
import { UserData } from '../types';
import { isOnline, formatLastSeen } from '../utils/format';
import { sectionLabelSx } from '../utils/styles';

interface ContactInfoProps {
  user: UserData;
  onClose: () => void;
}

const ContactInfo: React.FC<ContactInfoProps> = ({ user, onClose }) => {
  const navigate = useNavigate();
  const userIsOnline = isOnline(user.lastSeen);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#e8e8f0' }}>
          Contact Info
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#8888a8' }}>
          <Close />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Profile */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            px: 3,
          }}
        >
          <Avatar
            src={user.avatar}
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              fontSize: 48,
              background: 'linear-gradient(135deg, #7c4dff, #ff4081)',
              boxShadow: '0 0 30px rgba(124,77,255,0.3)',
            }}
          >
            {user.name?.[0]?.toUpperCase()}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#e8e8f0' }}>
            {user.name || user.username}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: userIsOnline ? '#00e676' : '#5555a0',
                boxShadow: userIsOnline
                  ? '0 0 6px rgba(0,230,118,0.5)'
                  : 'none',
              }}
            />
            <Typography variant="body2" sx={{ color: '#8888a8' }}>
              {formatLastSeen(user.lastSeen)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />

        {/* About */}
        <Box sx={{ p: 2.5 }}>
          <Typography
            variant="caption"
            sx={{ ...sectionLabelSx, color: '#7c4dff' }}
          >
            About
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: '#e8e8f0' }}>
            {user.bio || 'No bio yet'}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />

        {/* Email */}
        <Box sx={{ p: 2.5 }}>
          <Typography
            variant="caption"
            sx={{ ...sectionLabelSx, color: '#7c4dff' }}
          >
            Email
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: '#e8e8f0' }}>
            {user.email}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />

        {/* Username */}
        <Box sx={{ p: 2.5 }}>
          <Typography
            variant="caption"
            sx={{ ...sectionLabelSx, color: '#7c4dff' }}
          >
            Username
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: '#e8e8f0' }}>
            @{user.username}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderRadius: 3,
            borderColor: 'rgba(255,64,129,0.3)',
            color: '#ff4081',
            '&:hover': {
              borderColor: '#ff4081',
              bgcolor: 'rgba(255,64,129,0.06)',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );
};

export default ContactInfo;
